<!-- src/components/EntityForm.vue -->
<script setup>
import {defineProps, onMounted, ref, computed, watchEffect} from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import Textarea from "@/components/common/fields/Textarea.vue";
import InputText from "@/components/common/fields/InputText.vue";
import Select from "@/components/common/fields/Select.vue";
import InputDate from "@/components/common/fields/InputDate.vue";
import ActiveSwitch from "@/components/common/fields/ActiveSwitch.vue";
import Autocomplete from "@/components/common/fields/Autocomplete.vue";
import RadioButton from "@/components/common/fields/RadioButton.vue";
import ComboBox from "@/components/common/fields/ComboBox.vue";
import Treeview from "@/components/common/fields/Treeview.vue";
import SelectIcon from "@/components/common/fields/SelectIcon.vue";
import Upload from "@/components/common/fields/upload.vue";
import { isNamespaceExportDeclaration } from "typescript";

const props = defineProps({
    entity: Object,
    modelValue: Object,
    disabled: {
        type: Boolean,
        default: false
    },
    advancedFilter: {
        type: Boolean,
        default: false
    },
    fieldsToUse: {
        type: String,
        default: null
    }
});

const fieldsToRender = computed(() => {
    let fields = [];
    if (!props.entity) {
        return fields;
    }

    if (props.advancedFilter) {
        return props.entity.getAdvancedFilterFields(props.fieldsToUse);
    }

    if (props.fieldsToUse && props.entity[props.fieldsToUse]) {
        return props.entity[props.fieldsToUse];
    }

    return props.entity.getEditableFields();

})

const { t } = useI18n();
const route = useRoute();

const isCreatePage = ref(route.path.toLowerCase().includes("create"));
const emit = defineEmits(["update:modelValue", "field-change", "search"]);
const form = ref(null);
defineExpose({
    validate: () => form.value?.validate(),
    reset: () => form.value?.reset()
})

function handleSearch(field, query) {
    if (field.searchHandler) {
        emit("search", { handler: field.searchHandler, query, key: field.key });
    }
}

onMounted(() => {
    if (!isCreatePage.value || !props.modelValue) return;

    const fields = fieldsToRender.value;
    const hasStatusSwitch = fields.some(field => field.key === "status" && field.type === "activeSwitch");
    const hasActiveSwitch = fields.some(field => field.key === "active" && field.type === "activeSwitch");

    if (hasStatusSwitch) props.modelValue.status = true;
    if (hasActiveSwitch) props.modelValue.active = true;
});



function handleFieldChange(field) {
    emit("field-change", { field, value: props.modelValue[field.key] });
}

function updateField(key, value) {
    const updated = { ...props.modelValue, [key]: value };
    emit("update:modelValue", updated);
}

function isReadOnly(field) {
    return props.disabled || (isCreatePage.value ? field.disabledOnCreate : field.readOnly);
}

</script>

<template>
    <v-form ref="form">
        <v-row>
            <v-col
                v-for="field in fieldsToRender"
                :md="field.cols || 12" cols="12"
            >
                <div v-if="field.label && !['hidden', 'upload'].includes(field.type) && field.visible !== false" class="trezo-form-group">
                    <v-label class="d-block fw-medium text-black" :class="{ 'is-required': field.required }">
                        {{ t(field.label) }}
                    </v-label>
                </div>

                <InputText
                    v-if="['text', 'email', 'password', 'number'].includes(field.type)"
                    :type="field.type"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.placeholder ?? field.label)"
                    :required="field.required"
                    :maxlength="field.maxlength"
                    :notLength="field.notLength ?? false"
                    :readonly="isReadOnly(field)"
                    @blur="handleFieldChange(field)"
                />

                <Upload
                    v-else-if="field.type === 'upload' && field.visible !== false"
                    :model-value="modelValue[field.key]"
                    @update:model-value="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :titulo="field.titulo ? t(field.titulo) : ''"
                    :subtitulo="field.subtitulo ? t(field.subtitulo) : ''"
                    :preview-alt="field.previewAlt ? t(field.previewAlt) : ''"
                    :replace-text="field.replaceText ? t(field.replaceText) : ''"
                    :remove-text="field.removeText ? t(field.removeText) : ''"
                    :invalid-type-text="field.invalidTypeText ? t(field.invalidTypeText) : ''"
                    :max-size-text="field.maxSizeText ? t(field.maxSizeText) : ''"
                    :accept="field.accept"
                    :max-size="field.maxSize"
                    :required="field.required"
                    :readonly="isReadOnly(field)"
                />

                <Autocomplete
                    v-else-if="field.type === 'autocomplete' && field.visible !== false"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :items="field.options?.map(opt => ({
                        ...opt,
                        title: opt.label ?? opt.title,
                        value: opt.value
                    }))"
                    :required="field.required"
                    item-title="title"
                    item-value="value"
                    :readonly="isReadOnly(field)"
                    :multiple="field.multiple"

                    @search="(query) => handleSearch(field, query)"
                />

                <RadioButton
                    v-else-if="field.type === 'radioButton'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :options="field.options?.map(opt => ({
                        label: t(opt.label),
                        value: opt.value,
                        default: opt.default
                    }))"
                    :value="modelValue[field.key] ?? field.default"
                    :required="field.required"
                    :readonly="isReadOnly(field)"
                />

                <Select
                    v-else-if="field.type === 'select'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :items="field.options?.map(opt => ({
                      title: t(opt.label),
                      value: opt.value
                    }))"
                    :required="field.required"
                    item-title="title"
                    item-value="value"
                    :multiple="field.multiple"
                    :readonly="isReadOnly(field)"
                    :disabled="props.disabled || (isCreatePage && field.disabledOnCreate)"
                />

                <ComboBox
                    v-else-if="field.type === 'combobox'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :options="field.options?.map(opt => ({
                      label: t(opt.label),
                      value: opt.value
                    }))"
                    :required="field.required"
                    :multiple="field.multiple"
                    :readonly="isReadOnly(field)"
                />

                <InputDate
                    v-else-if="field.type === 'date'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    type="date"
                    :required="field.required"
                    :readonly="isReadOnly(field)"
                />

                <Textarea
                    v-else-if="field.type === 'textarea'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :label="t(field.label)"
                    :required="field.required"
                    :readonly="isReadOnly(field)"
                />

                <Treeview
                    v-else-if="field.type === 'Treeview'"
                    :type="field.type"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :items="field.options"
                    :required="field.required"
                    :maxlength="field.maxlength"
                    :readonly="isReadOnly(field)"
                    @blur="handleFieldChange(field)"
                />

                <ActiveSwitch
                    v-else-if="field.type === 'activeSwitch'"
                    :modelValue="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                    :disabled="isCreatePage"
                />
                <SelectIcon
                    v-else-if="field.type === 'selectIcon'"
                    v-model="modelValue[field.key]"
                    @update:modelValue="value => updateField(field.key, value)"
                />
            </v-col>
        </v-row>
    </v-form>
</template>
